import { KeyRound, LifeBuoy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSshKeys } from "../../api/queries.js";
import { DangerDialog } from "../../ui/confirm.js";
import { Dialog } from "../../ui/overlay.js";
import { Alert, Button, Card } from "../../ui/primitives.js";
import { useStartAction } from "./actions.js";
import { CopyValue } from "./status.js";
import { blockedReason, type ServerTabProps } from "./types.js";

/**
 * Shows a new root password once. CloudServer does not keep it: closing the
 * dialog is the last chance to copy it.
 */
export function RootPasswordDialog({
  password,
  onClose,
}: {
  password: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={password !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t("servers.password.title")}
      description={t("servers.password.lead")}
      footer={<Button onClick={onClose}>{t("servers.password.saved")}</Button>}
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-line bg-surface-2 px-3 py-2">
          {password && <CopyValue value={password} label={t("servers.password.label")} />}
        </div>
        <Alert tone="warn">{t("servers.password.once")}</Alert>
      </div>
    </Dialog>
  );
}

export function RescueTab(props: ServerTabProps) {
  const { t } = useTranslation();
  const { server, projectId } = props;
  const keys = useSshKeys(projectId);
  const { start, pending } = useStartAction(props);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [confirmReset, setConfirmReset] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const reason = blockedReason(props, t);
  const toggle = (id: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const showPassword = (result: { rootPassword: string | null }) =>
    setPassword(result.rootPassword);

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-4">
        <div className="flex gap-3">
          <LifeBuoy className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden />
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold">{t("servers.rescue.title")}</h2>
            <p className="text-sm text-muted">{t("servers.rescue.lead")}</p>
          </div>
        </div>
        {server.rescueEnabled ? (
          <>
            <Alert tone="warn">{t("servers.rescue.enabledNow")}</Alert>
            <div>
              <Button
                variant="secondary"
                disabled={reason !== null}
                busy={pending}
                onClick={() => start("disable-rescue")}
              >
                {t("servers.rescue.disable")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">{t("servers.rescue.keys")}</legend>
              {keys.data && keys.data.length === 0 && (
                <p className="text-sm text-muted">{t("servers.rescue.noKeys")}</p>
              )}
              {keys.data?.map((key) => (
                <label key={key.id} className="flex min-h-8 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={chosen.has(key.id)}
                    onChange={() => toggle(key.id)}
                    className="size-4 accent-accent"
                  />
                  <KeyRound className="size-4 text-muted" aria-hidden />
                  {key.name}
                </label>
              ))}
              <p className="text-xs text-muted">{t("servers.rescue.keysHint")}</p>
            </fieldset>
            <p className="text-sm text-muted">{t("servers.rescue.thenReboot")}</p>
            <div>
              <Button
                disabled={reason !== null}
                busy={pending}
                onClick={() =>
                  start("enable-rescue", { sshKeyIds: [...chosen] }, (result) => {
                    setChosen(new Set());
                    showPassword(result);
                  })
                }
              >
                {t("servers.rescue.enable")}
              </Button>
            </div>
          </>
        )}
        {reason && <p className="text-xs text-muted">{reason}</p>}
      </Card>

      <Card className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">{t("servers.password.resetTitle")}</h2>
        <p className="text-sm text-muted">{t("servers.password.resetLead")}</p>
        <div>
          <Button
            variant="danger"
            disabled={reason !== null || server.status !== "running"}
            onClick={() => setConfirmReset(true)}
          >
            {t("servers.password.reset")}
          </Button>
        </div>
        {server.status !== "running" && (
          <p className="text-xs text-muted">{t("servers.password.needsRunning")}</p>
        )}
      </Card>

      <DangerDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title={t("servers.password.confirmTitle", { name: server.name })}
        consequences={[t("servers.password.consequenceOld"), t("servers.password.consequenceKeys")]}
        actionLabel={t("servers.password.reset")}
        busy={pending}
        onConfirm={() =>
          start("reset-password", undefined, (result) => {
            setConfirmReset(false);
            showPassword(result);
          })
        }
      />
      <RootPasswordDialog password={password} onClose={() => setPassword(null)} />
    </div>
  );
}
